import { observer } from "mobx-react-lite";
import { ActionHandler } from "@/components/UI/tools";
import { appState } from "@/app/appState";
import { Field, Pill, StringSelectField } from "@/app/workspace/settings/ui";
import {
    defaultCreateArtifactSpec,
    isImplementedSideEffectType,
    placeholdersForSideEffectType,
    SIDE_EFFECT_TYPES,
    skillContentI18nKey,
    skillVariableNameI18nKey,
    unusedSideEffectTypes,
} from "./kvpRouteSpecs";

const I18nFieldPill = observer(({
    i18nKey,
    onOpenI18n,
}: {
    i18nKey: string;
    onOpenI18n?: (key: string) => void;
}) => (
    <Pill
        tone="accent"
        title={appState.t("settings.kvp.sideEffect.i18nFieldTitle")}
        onClick={onOpenI18n ? (e) => {
            e.stopPropagation();
            onOpenI18n(i18nKey);
        } : undefined}
    >
        {appState.t("settings.kvp.pill.i18n")}
    </Pill>
));

const SideEffectCard = observer(({
    spec,
    skillId,
    usedInPrompt,
    onChange,
    onRemove,
    onOpenI18n,
    onEnsureI18n,
}: {
    spec: Record<string, unknown>;
    skillId: string | null;
    usedInPrompt: Set<string>;
    onChange: (next: Record<string, unknown>) => void;
    onRemove: () => void;
    onOpenI18n?: (key: string) => void;
    onEnsureI18n?: (key: string) => Promise<void>;
}) => {
    const type = typeof spec.type === "string" ? spec.type : "";
    const known = isImplementedSideEffectType(type);
    const typeOptions = known
        ? SIDE_EFFECT_TYPES.map((id) => ({ id, name: id }))
        : [{ id: type, name: type || "?" }];
    const promptParams = placeholdersForSideEffectType(type);

    return (
        <div className="panel">
            <Field label={appState.t("settings.kvp.sideEffect.what")}>
                <StringSelectField
                    value={type}
                    onChange={(nextType) => {
                        if (nextType === "create_artifact") {
                            if (skillId && onEnsureI18n) {
                                void onEnsureI18n(skillVariableNameI18nKey(skillId)).then(() =>
                                    onEnsureI18n(skillContentI18nKey(skillId))
                                );
                            }
                            onChange(defaultCreateArtifactSpec(skillId));
                            return;
                        }
                        onChange({ ...spec, type: nextType });
                    }}
                    options={typeOptions}
                    disabled={!known}
                />
            </Field>
            {promptParams.length > 0 && (
                <Field label={appState.t("settings.kvp.sideEffect.promptParams")}>
                    <div className="flex flex-row flex-wrap gap-[.25rem]">
                        {promptParams.map((name) => (
                            <span
                                key={name}
                                className={`code-chip ${usedInPrompt.has(name) ? "" : "code-chip-muted"}`}
                            >
                                {`{${name}}`}
                            </span>
                        ))}
                    </div>
                </Field>
            )}
            {known && skillId && (
                <>
                    <Field label={appState.t("settings.kvp.sideEffect.noteTitle")}>
                        <I18nFieldPill
                            i18nKey={skillVariableNameI18nKey(skillId)}
                            onOpenI18n={onOpenI18n}
                        />
                    </Field>
                    <Field label={appState.t("settings.kvp.sideEffect.startingText")}>
                        <I18nFieldPill
                            i18nKey={skillContentI18nKey(skillId)}
                            onOpenI18n={onOpenI18n}
                        />
                    </Field>
                </>
            )}
            <ActionHandler
                title={appState.t("settings.kvp.removeSideEffect")}
                funq={onRemove}
                className="btn-danger btn-sm"
            >
                {appState.t("settings.kvp.removeSideEffect")}
            </ActionHandler>
        </div>
    );
});

export const SettingsKeyLinks = observer(({
    label,
    keys,
    onOpen,
}: {
    label: string;
    keys: string[];
    onOpen?: (key: string) => void;
}) => {
    if (keys.length === 0) return null;
    return (
        <div className="flex flex-col gap-[.2rem]">
            <small className="field-label">{label}</small>
            <div className="flex flex-col gap-[.15rem]">
                {keys.map((key) => (
                    <button
                        key={key}
                        type="button"
                        className="key-link"
                        onClick={() => onOpen?.(key)}
                        disabled={!onOpen}
                    >
                        {key}
                    </button>
                ))}
            </div>
        </div>
    );
});

export const KvpSideEffectsEditor = observer(({
    skillId,
    specs,
    usedInPrompt,
    onChange,
    onOpenI18n,
    onEnsureI18n,
}: {
    skillId: string | null;
    specs: Record<string, unknown>[];
    usedInPrompt: Set<string>;
    onChange: (specs: Record<string, unknown>[]) => void;
    onOpenI18n?: (key: string) => void;
    onEnsureI18n?: (key: string) => Promise<void>;
}) => {
    const unusedTypes = unusedSideEffectTypes(specs);

    return (
        <div className="flex flex-col gap-2">
            {specs.map((spec, index) => (
                <SideEffectCard
                    key={index}
                    spec={spec}
                    skillId={skillId}
                    usedInPrompt={usedInPrompt}
                    onOpenI18n={onOpenI18n}
                    onEnsureI18n={onEnsureI18n}
                    onChange={(next) => {
                        onChange(specs.map((item, i) => (i === index ? next : item)));
                    }}
                    onRemove={() => {
                        onChange(specs.filter((_, i) => i !== index));
                    }}
                />
            ))}
            {unusedTypes.length > 0 && (
                <ActionHandler
                    title={appState.t("settings.kvp.addSideEffect")}
                    funq={async () => {
                        const nextType = unusedTypes[0];
                        const added = nextType === "create_artifact"
                            ? defaultCreateArtifactSpec(skillId)
                            : { type: nextType };
                        if (nextType === "create_artifact" && skillId && onEnsureI18n) {
                            await onEnsureI18n(skillVariableNameI18nKey(skillId));
                            await onEnsureI18n(skillContentI18nKey(skillId));
                        }
                        onChange([...specs, added]);
                    }}
                >
                    {appState.t("settings.kvp.addSideEffect")}
                </ActionHandler>
            )}
        </div>
    );
});
